import type { UseQueryResult } from '@tanstack/react-query'

import type { PricePrefillBid, PricePrefillBidsResponse } from '@/features/bidding/endpoints/pricePrefillBids'
import type { BidsLatestPricesQueryParams } from '@/features/bidding/hooks/usePricePrefillBidsQuery'
import { usePricePrefillBidsQuery } from '@/features/bidding/hooks/usePricePrefillBidsQuery'
import type { UseQueryOptions } from '@/types/queries'

export type { BidsLatestPricesQueryParams } from '@/features/bidding/hooks/usePricePrefillBidsQuery'

export type UseBidsLatestPricesQueryResult = Omit<UseQueryResult, 'data'> & {
  prices: number[] | null
}

export const useBidsLatestPricesQuery = (
  params: BidsLatestPricesQueryParams,
  options?: UseQueryOptions<PricePrefillBidsResponse>,
): UseBidsLatestPricesQueryResult => {
  const { data, ...queryResult } = usePricePrefillBidsQuery(params, options)

  if (!data) {
    return { prices: null, ...queryResult }
  }

  return { prices: mostRecentlySourcedPrices(data), ...queryResult }
}

// Aggregated pricing (FCR family): a single price ladder is applied to every activation group.
// The endpoint resolves each group independently, so among the groups that actually have prices
// we reuse the ladder whose prefill comes from the most recent source delivery day.
const mostRecentlySourcedPrices = (prefillBids: PricePrefillBidsResponse): number[] => {
  const mostRecent = Object.values(prefillBids)
    .filter((prefillBid) => hasPrices(prefillBid))
    .reduce<PricePrefillBid | null>((latest, current) => preferMoreRecentSourceDay(latest, current), null)
  return mostRecent?.offeredPrices ?? []
}

const hasPrices = (prefillBid: PricePrefillBid): boolean => prefillBid.offeredPrices.length > 0

const preferMoreRecentSourceDay = (latest: PricePrefillBid | null, current: PricePrefillBid): PricePrefillBid =>
  !latest || current.sourceDeliveryDay > latest.sourceDeliveryDay ? current : latest
